/**
 * Unified Integration Service - Clean Version
 * Connects TestsInterviewsScreen, NotificationsScreen, ProfessionalDashboardScreen, and AssessmentResultsScreen
 * with real-time data sharing, seamless navigation, and comprehensive business functionality
 */

// Mock AsyncStorage for environments where it's not available
let AsyncStorage;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (error) {
  console.warn('AsyncStorage not available, using mock implementation');
  AsyncStorage = {
    getItem: async (key) => null,
    setItem: async (key, value) => {},
    multiGet: async (keys) => keys.map(key => [key, null]),
    removeItem: async (key) => {}
  };
}

// Mock NetInfo for environments where it's not available
let NetInfo;
try {
  NetInfo = require('@react-native-community/netinfo').default;
} catch (error) {
  console.warn('NetInfo not available, using mock implementation');
  NetInfo = {
    addEventListener: (callback) => {
      // Call immediately with online status
      callback({ isConnected: true, type: 'wifi' });
      // Return unsubscribe function
      return () => {};
    }
  };
}

class UnifiedIntegrationService {
  constructor() {
    this.isInitialized = false;
    this.isOnline = true;
    this.subscribers = new Map();
    this.pendingSync = [];
    this.realTimeData = {
      notifications: [],
      dashboardMetrics: {},
      assessmentResults: [],
      userProfile: {},
      businessMetrics: {}
    };
  }

  // Initialize the service
  async initialize() {
    try {
      console.log('🚀 Initializing Unified Integration Service...');
      
      // Setup network monitoring
      await this.setupNetworkMonitoring();
      
      // Load cached data
      await this.loadCachedData();
      
      // Initialize real-time monitoring
      await this.initializeRealTimeMonitoring();
      
      // Setup business automation
      await this.setupBusinessAutomation();
      
      this.isInitialized = true;
      console.log('✅ Unified Integration Service initialized successfully');
      
      // Notify all subscribers
      this.notifySubscribers('service_initialized', { status: 'ready' });
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Unified Integration Service:', error);
      return false;
    }
  }

  // Network monitoring setup
  async setupNetworkMonitoring() {
    try {
      NetInfo.addEventListener(state => {
        const wasOnline = this.isOnline;
        this.isOnline = state.isConnected;
        
        if (!wasOnline && this.isOnline) {
          console.log('🌐 Network restored, syncing pending data...');
          this.syncPendingData();
        }
        
        this.notifySubscribers('network_status_changed', { 
          isOnline: this.isOnline,
          connectionType: state.type 
        });
      });
    } catch (error) {
      console.warn('Network monitoring setup failed:', error);
    }
  }

  // Load cached data from AsyncStorage
  async loadCachedData() {
    try {
      const keys = [
        'notifications_data',
        'dashboard_metrics',
        'assessment_results',
        'user_profile',
        'business_metrics'
      ];
      
      const cachedData = await AsyncStorage.multiGet(keys);
      
      cachedData.forEach(([key, value]) => {
        if (value) {
          const dataKey = key.replace('_data', '').replace('_', '');
          this.realTimeData[dataKey] = JSON.parse(value);
        }
      });
      
      console.log('📦 Cached data loaded successfully');
    } catch (error) {
      console.error('❌ Error loading cached data:', error);
    }
  }

  // Initialize real-time monitoring
  async initializeRealTimeMonitoring() {
    // Guard: start the timers only once. Without this, every call stacks
    // another setInterval, so syncs fire N× and hammer the server.
    if (this._monitoringStarted) return;
    this._monitoringStarted = true;
    // Start periodic sync every 30 seconds
    setInterval(() => {
      if (this.isOnline) {
        this.performRealTimeSync();
      }
    }, 30000);
    
    // Start metrics update every 5 seconds
    setInterval(() => {
      this.updateRealTimeMetrics();
    }, 5000);
  }

  // Setup business automation
  async setupBusinessAutomation() {
    // Auto-create notifications for assessment completions
    this.subscribe('assessment_completed', (data) => {
      this.createNotification({
        type: 'assessment_completed',
        title: 'Assessment Completed',
        message: `You've completed the ${data.assessmentType} assessment with a score of ${data.score}%`,
        actionRoute: 'AssessmentResults',
        actionParams: { assessmentId: data.id }
      });
    });
    
    // Auto-update dashboard metrics
    this.subscribe('assessment_completed', (data) => {
      this.updateDashboardMetrics({
        assessmentsCompleted: this.realTimeData.dashboardMetrics.assessmentsCompleted + 1,
        averageScore: this.calculateNewAverageScore(data.score),
        lastActivity: new Date().toISOString()
      });
    });
    
    // Auto-schedule interview reminders
    this.subscribe('interview_scheduled', (data) => {
      this.scheduleInterviewReminder(data);
    });
  }

  // Subscribe to events
  subscribe(event, callback) {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, []);
    }
    this.subscribers.get(event).push(callback);
  }

  // Unsubscribe from events
  unsubscribe(event, callback) {
    if (this.subscribers.has(event)) {
      const callbacks = this.subscribers.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Notify subscribers
  notifySubscribers(event, data) {
    if (this.subscribers.has(event)) {
      this.subscribers.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in subscriber callback for ${event}:`, error);
        }
      });
    }
  }

  // NOTIFICATIONS MANAGEMENT
  async createNotification(notificationData) {
    try {
      const notification = {
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...notificationData,
        timestamp: new Date().toISOString(),
        isRead: false,
        actionable: !!notificationData.actionRoute
      };
      
      this.realTimeData.notifications.unshift(notification);
      
      // Save to AsyncStorage
      await AsyncStorage.setItem('notifications_data', JSON.stringify(this.realTimeData.notifications));
      
      // Notify subscribers
      this.notifySubscribers('notification_created', notification);
      this.notifySubscribers('notifications_updated', this.realTimeData.notifications);
      
      console.log('🔔 Notification created:', notification.title);
      return notification;
    } catch (error) {
      console.error('❌ Error creating notification:', error);
      return null;
    }
  }

  async markNotificationAsRead(notificationId) {
    try {
      const notification = this.realTimeData.notifications.find(n => n.id === notificationId);
      if (notification) {
        notification.isRead = true;
        await AsyncStorage.setItem('notifications_data', JSON.stringify(this.realTimeData.notifications));
        this.notifySubscribers('notification_read', notification);
        this.notifySubscribers('notifications_updated', this.realTimeData.notifications);
      }
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
    }
  }

  async markAllNotificationsAsRead() {
    try {
      this.realTimeData.notifications.forEach(notification => {
        notification.isRead = true;
      });
      
      await AsyncStorage.setItem('notifications_data', JSON.stringify(this.realTimeData.notifications));
      this.notifySubscribers('all_notifications_read', {});
      this.notifySubscribers('notifications_updated', this.realTimeData.notifications);
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
    }
  }

  getNotifications(filters = {}) {
    let notifications = [...this.realTimeData.notifications];
    
    if (filters.unreadOnly) {
      notifications = notifications.filter(n => !n.isRead);
    }
    
    if (filters.type) {
      notifications = notifications.filter(n => n.type === filters.type);
    }
    
    return notifications.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  getUnreadNotificationCount() {
    return this.realTimeData.notifications.filter(n => !n.isRead).length;
  }

  // DASHBOARD METRICS MANAGEMENT
  async updateDashboardMetrics(updates) {
    try {
      this.realTimeData.dashboardMetrics = {
        ...this.realTimeData.dashboardMetrics,
        ...updates,
        lastUpdated: new Date().toISOString()
      };
      
      await AsyncStorage.setItem('dashboard_metrics', JSON.stringify(this.realTimeData.dashboardMetrics));
      this.notifySubscribers('dashboard_metrics_updated', this.realTimeData.dashboardMetrics);
      
      console.log('📊 Dashboard metrics updated');
    } catch (error) {
      console.error('❌ Error updating dashboard metrics:', error);
    }
  }

  getDashboardMetrics() {
    return {
      assessmentsCompleted: 0,
      averageScore: 0,
      interviewsScheduled: 0,
      applicationsSubmitted: 0,
      profileStrength: 0,
      skillsImproved: 0,
      certificatesEarned: 0,
      networkConnections: 0,
      ...this.realTimeData.dashboardMetrics
    };
  }

  async calculateDashboardInsights() {
    const metrics = this.getDashboardMetrics();
    const assessmentResults = this.getAssessmentResults();
    
    return {
      performanceTrend: this.calculatePerformanceTrend(assessmentResults),
      skillGaps: this.identifySkillGaps(assessmentResults),
      recommendations: this.generateRecommendations(metrics, assessmentResults),
      nextActions: this.suggestNextActions(metrics),
      careerProgress: this.calculateCareerProgress(metrics)
    };
  }

  // ASSESSMENT RESULTS MANAGEMENT
  async saveAssessmentResult(assessmentData) {
    try {
      const result = {
        id: assessmentData.id || `assessment_${Date.now()}`,
        assessmentType: assessmentData.type,
        assessmentTitle: assessmentData.title || `${assessmentData.type} Assessment`,
        score: assessmentData.score,
        passed: assessmentData.score >= (assessmentData.passingScore || 70),
        correctAnswers: assessmentData.correctAnswers,
        totalQuestions: assessmentData.totalQuestions,
        timeSpent: assessmentData.timeSpent,
        timestamp: new Date().toISOString(),
        certificateEligible: assessmentData.score >= 80,
        detailedResults: assessmentData.detailedResults || [],
        skillsAssessed: assessmentData.skillsAssessed || [],
        recommendations: assessmentData.recommendations || []
      };
      
      this.realTimeData.assessmentResults.unshift(result);
      
      // Keep only last 50 results
      if (this.realTimeData.assessmentResults.length > 50) {
        this.realTimeData.assessmentResults = this.realTimeData.assessmentResults.slice(0, 50);
      }
      
      await AsyncStorage.setItem('assessment_results', JSON.stringify(this.realTimeData.assessmentResults));
      
      // Trigger events
      this.notifySubscribers('assessment_completed', result);
      this.notifySubscribers('assessment_results_updated', this.realTimeData.assessmentResults);
      
      console.log('📝 Assessment result saved:', result.assessmentTitle);
      return result;
    } catch (error) {
      console.error('❌ Error saving assessment result:', error);
      return null;
    }
  }

  getAssessmentResults(filters = {}) {
    let results = [...this.realTimeData.assessmentResults];
    
    if (filters.type) {
      results = results.filter(r => r.assessmentType === filters.type);
    }
    
    if (filters.passed !== undefined) {
      results = results.filter(r => r.passed === filters.passed);
    }
    
    if (filters.limit) {
      results = results.slice(0, filters.limit);
    }
    
    return results.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  // Sync pending data
  async syncPendingData() {
    try {
      console.log('🔄 Syncing pending data...');
      // Implementation for syncing offline data
      this.pendingSync = [];
    } catch (error) {
      console.error('❌ Error syncing pending data:', error);
    }
  }

  // Perform real-time sync
  async performRealTimeSync() {
    try {
      // Implementation for real-time data sync
      console.log('🔄 Performing real-time sync...');
    } catch (error) {
      console.error('❌ Error performing real-time sync:', error);
    }
  }

  // Update real-time metrics
  updateRealTimeMetrics() {
    try {
      // Implementation for updating metrics
      const timestamp = new Date().toISOString();
      this.notifySubscribers('metrics_updated', { timestamp });
    } catch (error) {
      console.error('❌ Error updating real-time metrics:', error);
    }
  }

  // Calculate new average score
  calculateNewAverageScore(newScore) {
    const currentMetrics = this.getDashboardMetrics();
    const currentTotal = currentMetrics.averageScore * (currentMetrics.assessmentsCompleted - 1);
    return Math.round((currentTotal + newScore) / currentMetrics.assessmentsCompleted);
  }

  // Schedule interview reminder
  scheduleInterviewReminder(data) {
    console.log('⏰ Scheduling interview reminder for:', data.interviewId);
    // Implementation for scheduling reminders
  }

  // Register interview session
  async registerInterviewSession(session) {
    try {
      console.log('📝 Registering interview session:', session.interviewId);
      // Store session data
      this.realTimeData.currentInterview = session;
      return true;
    } catch (error) {
      console.error('❌ Error registering interview session:', error);
      return false;
    }
  }

  // Notify interview started
  async notifyInterviewStarted(interviewId) {
    try {
      console.log('🎬 Interview started:', interviewId);
      this.notifySubscribers('interview_started', { interviewId });
      return true;
    } catch (error) {
      console.error('❌ Error notifying interview started:', error);
      return false;
    }
  }

  // Notify recording started
  async notifyRecordingStarted(interviewId, questionId) {
    try {
      console.log('🎥 Recording started:', { interviewId, questionId });
      this.notifySubscribers('recording_started', { interviewId, questionId });
      return true;
    } catch (error) {
      console.error('❌ Error notifying recording started:', error);
      return false;
    }
  }

  // Store interview response
  async storeInterviewResponse(interviewId, responseData) {
    try {
      console.log('💾 Storing interview response:', interviewId);
      if (!this.realTimeData.interviewResponses) {
        this.realTimeData.interviewResponses = {};
      }
      if (!this.realTimeData.interviewResponses[interviewId]) {
        this.realTimeData.interviewResponses[interviewId] = [];
      }
      this.realTimeData.interviewResponses[interviewId].push(responseData);
      return true;
    } catch (error) {
      console.error('❌ Error storing interview response:', error);
      return false;
    }
  }

  // Notify question changed
  async notifyQuestionChanged(interviewId, questionId, questionIndex) {
    try {
      console.log('❓ Question changed:', { interviewId, questionId, questionIndex });
      this.notifySubscribers('question_changed', { interviewId, questionId, questionIndex });
      return true;
    } catch (error) {
      console.error('❌ Error notifying question changed:', error);
      return false;
    }
  }

  // Store interview results
  async storeInterviewResults(interviewId, results) {
    try {
      console.log('🏆 Storing interview results:', interviewId);
      if (!this.realTimeData.interviewResults) {
        this.realTimeData.interviewResults = {};
      }
      this.realTimeData.interviewResults[interviewId] = results;
      
      // Save to AsyncStorage
      await AsyncStorage.setItem('interview_results', JSON.stringify(this.realTimeData.interviewResults));
      
      this.notifySubscribers('interview_results_stored', { interviewId, results });
      return true;
    } catch (error) {
      console.error('❌ Error storing interview results:', error);
      return false;
    }
  }

  // Helper methods for business intelligence
  calculatePerformanceTrend(results) {
    if (results.length < 2) return 'stable';
    
    const recent = results.slice(0, 5);
    const older = results.slice(5, 10);
    
    if (recent.length === 0 || older.length === 0) return 'stable';
    
    const recentAvg = recent.reduce((sum, r) => sum + r.score, 0) / recent.length;
    const olderAvg = older.reduce((sum, r) => sum + r.score, 0) / older.length;
    
    const improvement = recentAvg - olderAvg;
    
    if (improvement > 5) return 'improving';
    if (improvement < -5) return 'declining';
    return 'stable';
  }

  identifySkillGaps(results) {
    const skillScores = {};
    
    results.forEach(result => {
      if (result.skillsAssessed) {
        result.skillsAssessed.forEach(skill => {
          if (!skillScores[skill.name]) {
            skillScores[skill.name] = [];
          }
          skillScores[skill.name].push(skill.score);
        });
      }
    });
    
    const gaps = [];
    Object.keys(skillScores).forEach(skill => {
      const scores = skillScores[skill];
      const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
      
      if (average < 70) {
        gaps.push({
          skill,
          averageScore: Math.round(average),
          assessmentCount: scores.length,
          priority: average < 50 ? 'high' : 'medium'
        });
      }
    });
    
    return gaps.sort((a, b) => a.averageScore - b.averageScore);
  }

  generateRecommendations(metrics, results) {
    const recommendations = [];
    
    // Assessment recommendations
    if (metrics.assessmentsCompleted < 5) {
      recommendations.push({
        type: 'assessment',
        title: 'Complete More Assessments',
        description: 'Take additional assessments to build a comprehensive skill profile',
        priority: 'medium',
        actionRoute: 'TestsInterviews'
      });
    }
    
    // Score improvement recommendations
    if (metrics.averageScore < 75) {
      recommendations.push({
        type: 'improvement',
        title: 'Focus on Skill Development',
        description: 'Your average score suggests room for improvement in key areas',
        priority: 'high',
        actionRoute: 'SkillDevelopment'
      });
    }
    
    return recommendations;
  }

  suggestNextActions(metrics) {
    const actions = [];
    
    if (metrics.profileStrength < 80) {
      actions.push({
        title: 'Complete Your Profile',
        description: 'Add missing information to reach 100% profile strength',
        route: 'Profile',
        urgency: 'medium'
      });
    }
    
    return actions;
  }

  calculateCareerProgress(metrics) {
    return {
      currentLevel: 'Intermediate',
      nextLevel: 'Advanced',
      progressPercentage: 65,
      skillsToImprove: ['Leadership', 'System Design'],
      estimatedTimeToNext: '3-6 months'
    };
  }

  // Reset service
  reset() {
    this.isInitialized = false;
    this.subscribers.clear();
    this.pendingSync = [];
    this.realTimeData = {
      notifications: [],
      dashboardMetrics: {},
      assessmentResults: [],
      userProfile: {},
      businessMetrics: {}
    };
  }
}

export default UnifiedIntegrationService;
